import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import Storefront from "./Storefront.tsx";
import Operations from "./Operations.tsx";

const API_BASE = "/api/commerce-ops-proof";
const ASSETS = {
  "HW-TRAVEL-KIT": "https://staging.obto.co/ms/filereader.bto?n=mtovwxov4acfaddd_staging_commerce-ops-proof_commerce-ops-proof-coastal-travel-kit.png",
  "HW-DESK-LAMP": "https://staging.obto.co/ms/filereader.bto?n=mtovx8ik940936e8_staging_commerce-ops-proof_commerce-ops-proof-wattle-desk-lamp.png"
};

async function api(path, options) {
  const response = await fetch(API_BASE + path, options);
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) throw new Error((options?.method || "GET") + " " + API_BASE + path + " returned " + (contentType || "non-JSON"));
  const result = await response.json();
  if (!response.ok || !result.ok) throw new Error(result.error || "API request failed");
  return result;
}

function BuildEvidence({ onOpenStore }) {
  return <div className="build-page">
    <section className="build-hero"><span className="kicker mint">Built through chat · governed by OBTO</span><h1>From business prompt<br />to working software.</h1><p>This screen is the bridge for the making-of video: the prompt defines the outcome, OBTO MCP creates the application artifacts, and the published product proves the result.</p></section>
    <section className="build-grid">
      <article className="prompt-card"><div className="chat-label"><span>DV</span><strong>Business owner prompt</strong></div><blockquote>Use OBTO to build an Australian dropshipping store with a customer storefront and an operations workspace. Orders below 30% margin must require named approval before fulfillment.</blockquote></article>
      <article className="build-flow"><div><span>01</span><strong>Understand</strong><small>Translate the business request into a build contract and route manifest.</small></div><div><span>02</span><strong>Build</strong><small>Create server logic, public APIs, React modules, styles, and original product media.</small></div><div><span>03</span><strong>Deploy</strong><small>Produce the Vite bundle and publish it to the canonical OBTO host.</small></div><div><span>04</span><strong>Verify</strong><small>Call every route, exercise checkout and approval, and inspect desktop/mobile browser evidence.</small></div></article>
      <article className="artifact-card"><span className="kicker mint">Live application contract</span><h2>What OBTO produced</h2><ul><li><strong>Customer surface</strong><span>Catalog, product detail, cart, checkout, order confirmation</span></li><li><strong>Operator surface</strong><span>Order queue, policy gate, supplier state, CRM timeline, drafts</span></li><li><strong>Control layer</strong><span>Named approval, policy version, persisted audit receipts</span></li><li><strong>Deployment</strong><span>Canonical public host with production bundle and JSON API</span></li></ul></article>
      <article className="claim-card"><span className="kicker">Public claim boundary</span><h2>What this demonstrates</h2><p>A synthetic but functioning commerce workflow built and deployed through OBTO MCP, with a consequential decision bounded by policy and recorded against a named operator.</p><p>It does not claim autonomous ecommerce operations or customer business outcomes.</p><button className="primary" onClick={onOpenStore}>Open the product OBTO built</button></article>
    </section>
  </div>;
}

function App() {
  const initial = new URLSearchParams(window.location.search).get("view");
  const [view, setView] = useState(initial === "ops" || initial === "build" ? initial : "store");
  const [data, setData] = useState(null);
  const [cart, setCart] = useState([]);
  const [lastOrder, setLastOrder] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const navigate = (next) => {
    setView(next);
    window.history.replaceState({}, "", next === "store" ? window.location.pathname : window.location.pathname + "?view=" + next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const refresh = async () => {
    try {
      setError("");
      const next = await api("/dashboard");
      setData(next);
      return next;
    } catch (err) {
      setError(err.message);
      return null;
    }
  };

  useEffect(() => { refresh(); }, []);

  const checkout = async (items) => {
    if (!items.length) return false;
    try {
      setBusy("checkout");
      setError("");
      const result = await api("/sandbox-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items })
      });
      setLastOrder(result.order);
      setCart([]);
      await refresh();
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setBusy("");
    }
  };

  const approve = async (orderId) => {
    try {
      setBusy("approve");
      setError("");
      await api("/finalize-sandbox", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId, approver: "Maya Chen — Operations Lead" })
      });
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy("");
    }
  };

  return (
    <div className={"app " + view}>
      <div className="demo-ribbon">Interactive sandbox · products and people are fictional · no payment is taken</div>
      <header className="site-header">
        <button className="brand" onClick={() => navigate("store")}><span>H</span><div><strong>Harbor & Wattle</strong><small>Field Goods · Australia</small></div></button>
        <nav aria-label="Primary">
          <button className={view === "store" ? "active" : ""} onClick={() => navigate("store")}>Storefront</button>
          <button className={view === "ops" ? "active" : ""} onClick={() => navigate("ops")}>Operations</button>
          <button className={view === "build" ? "active" : ""} onClick={() => navigate("build")}>Built with OBTO</button>
        </nav>
        <div className="header-proof"><span></span> Live API</div>
      </header>
      {!data && !error ? <div className="loading-screen"><span></span><p>Loading live commerce state…</p></div> : error && !data ? <div className="fatal-error"><h2>The live commerce API did not load.</h2><p>{error}</p><button className="primary" onClick={refresh}>Retry</button></div> : <>
        {view === "store" && <Storefront data={data} assets={ASSETS} cart={cart} setCart={setCart} lastOrder={lastOrder} busy={busy} error={error} onCheckout={checkout} onOpenOperations={() => navigate("ops")} />}
        {view === "ops" && <Operations data={data} busy={busy} error={error} onApprove={approve} onRefresh={refresh} onOpenStore={() => navigate("store")} />}
        {view === "build" && <BuildEvidence onOpenStore={() => navigate("store")} />}
      </>}
      <footer><div><strong>Harbor & Wattle</strong><span>Original synthetic commerce proof</span></div><div>Built through chat. Run on OBTO.</div></footer>
    </div>
  );
}

const mount = () => {
  const element = document.getElementById("root");
  if (element) createRoot(element).render(<App />);
};
document.readyState === "loading"
  ? document.addEventListener("DOMContentLoaded", mount, { once: true })
  : mount();